import React, { useContext } from "react";
import { UserContext, LanguageContext } from "../../../../Context";
import Messages from "./components/Messages";
import Days from "./components/Days";
import TodayShifts from "./components/TodayShifts";
import StudentNotes from "./components/StudentNotes";
import PendingLeaveApprovals from "./components/PendingLeaveApprovals";
import MarketingWidget from "./components/MarketingWidget";
import { useParams } from "react-router-dom";


/**
 * Home dashboard page component.
 * Displays greeting, today's shifts, schedule, messages, and student notes.
 * Uses tailwind utility classes with flat premium design language.
 *
 * @author Forge
 * @date 2026-04-08
 * @version 2.0
 */
export default function Home() {
  const { user, checkRoles } = useContext(UserContext);
  const { l, language } = useContext(LanguageContext);
  const { startOfWeek } = useParams();
  const [hasLessons, setHasLessons] = React.useState(true);
  const [hasPendingLeave, setHasPendingLeave] = React.useState(false);

  const getGreeting = () => {
    const hour = new Date().getHours();
    let key = "goodEvening";
    if (hour < 12) key = "goodMorning";
    else if (hour < 18) key = "goodAfternoon";
    return l[key] ? l[key][language] : "Welcome";
  };

  const todayStr = new Date().toLocaleDateString(language === 0 ? "en-US" : "vi-VN", {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });

  const isTeacher = checkRoles?.(["Teacher", "Teaching assistant", "Foreign Teacher"]);
  const isManager = checkRoles?.(["Manager", "Admin", "Owner", "Human resources"]);
  const isMarketing = checkRoles?.("Marketing");
  const showSchedule = isTeacher && hasLessons;

  const marketingVisible = isMarketing;
  const extras = [hasPendingLeave, marketingVisible].filter(Boolean).length;
  const gridCols = showSchedule
    ? extras >= 2 ? 'grid-cols-5' : extras === 1 ? 'grid-cols-4' : 'grid-cols-3'
    : extras >= 2 ? 'grid-cols-4' : extras === 1 ? 'grid-cols-3' : 'grid-cols-2';

  return (
    <div className="p-6 max-w-[1600px] mx-auto min-h-screen bg-[#f8fafc] text-[#1e293b]">
      <div className="flex justify-between items-start mb-6 pb-3 border-b border-[#e2e8f0] gap-8">
        <div className="flex flex-col">
          <h1 className={`font-heading text-[2rem] font-semibold text-teal-800 tracking-tight m-0`}>
            {getGreeting()}, {user.name}
          </h1>
          <div className="text-[1.05rem] text-[#64748b] font-medium mt-1">
            {todayStr}
          </div>
        </div>

        {/* Daily Shifts */}
        <div className="flex-1 max-w-[650px] min-h-[120px]">
          <TodayShifts user={user} />
        </div>
      </div>

      <div className={`grid gap-6 items-start ${gridCols}`}>
        {isTeacher && (
          <div className={`${!hasLessons ? 'hidden' : ''}`}>
            <Days startOfWeek={startOfWeek} onDataLoaded={(count) => setHasLessons(count > 0)} />
          </div>
        )}

        <div>
          <Messages />
        </div>

        <div>
          <StudentNotes />
        </div>

        {isManager && (
          <div className={!hasPendingLeave ? 'hidden' : ''}>
            <PendingLeaveApprovals onDataLoaded={(count) => setHasPendingLeave(count > 0)} />
          </div>
        )}

        {isMarketing && (
          <MarketingWidget />
        )}
      </div>
    </div>
  );
}
